import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, Check } from 'lucide-react';
import { QboAccount } from '../../../api/types';
import vendorApi from '../../../api/vendorApi';
import { useQuery } from '@tanstack/react-query';

interface QBOAccountSelectorProps {
    currentAccountId?: string | null;
    onSelect: (account: QboAccount) => void;
    isOpen: boolean;
    onClose: () => void;
    businessId?: string;
    allowedAccountTypes?: string[];
}

const QBOAccountSelector: React.FC<QBOAccountSelectorProps> = ({ currentAccountId, onSelect, isOpen, onClose, businessId, allowedAccountTypes }) => {
    const [activeType, setActiveType] = useState<string>('');
    const [searchTerm, setSearchTerm] = useState('');

    // Fetch QBO Accounts
    const { data: qboData, isLoading } = useQuery({
        queryKey: ['qboAccounts', businessId],
        queryFn: () => vendorApi.getQBOAccounts(businessId),
    });

    const normalizedAllowedTypes = useMemo(
        () => (allowedAccountTypes || []).map((value) => value.trim().toLowerCase()).filter(Boolean),
        [allowedAccountTypes]
    );

    const groups = useMemo(() => {
        const rawGroups = qboData?.groups || {};
        if (normalizedAllowedTypes.length === 0) return rawGroups;
        return Object.fromEntries(
            Object.entries(rawGroups).filter(([type]) => normalizedAllowedTypes.includes(String(type).trim().toLowerCase()))
        );
    }, [qboData, normalizedAllowedTypes]);
    const accountTypes = useMemo(() => Object.keys(groups).sort(), [groups]);

    // Initial Type Selection
    useEffect(() => {
        if (!activeType && accountTypes.length > 0) {
            // Try to find the type of the current account
            if (currentAccountId) {
                for (const type of accountTypes) {
                    const found = groups[type].find(a => a.data.account_id === currentAccountId);
                    if (found) {
                        setActiveType(type);
                        return;
                    }
                }
            }
            setActiveType(accountTypes[0]);
        }
    }, [accountTypes, activeType, currentAccountId, groups]);

    // Flatten accounts for search or get current group
    const displayAccounts = useMemo(() => {
        if (!qboData) return [];

        if (searchTerm) {
            // Search all accounts across all groups
            const allAccounts = Object.values(groups).flat();
            const lowerTerm = searchTerm.toLowerCase();
            return allAccounts.filter(a =>
                (a.data.name?.toLowerCase().includes(lowerTerm) || false) ||
                (a.data.fully_qualified_name?.toLowerCase().includes(lowerTerm) || false) ||
                (a.data.account_id?.toLowerCase().includes(lowerTerm) || false)
            );
        }

        // Return accounts for active type
        return groups[activeType] || [];
    }, [groups, activeType, searchTerm, qboData]);

    if (!isOpen) return null;

    return createPortal(
        <div className="fixed inset-0 z-[120] overflow-y-auto" aria-labelledby="qbo-account-selector-title" role="dialog" aria-modal="true">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={onClose}></div>
                <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                    {/* Header */}
                    <div className="px-4 pt-5 pb-4 sm:p-6 sm:pb-4 border-b border-gray-100">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg leading-6 font-medium text-gray-900" id="qbo-modal-title">Select QBO Account</h3>
                            <button onClick={onClose} className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none">
                                <span className="sr-only">Close</span>
                                <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* Search Input */}
                        <div className="relative rounded-md shadow-sm mb-4">
                            <input
                                type="text"
                                className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-3 pr-10 sm:text-sm border-gray-300 rounded-md py-2"
                                placeholder="Search QBO accounts..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                autoFocus
                            />
                        </div>

                        {/* Tabs (Only show if not searching) */}
                        {!searchTerm && (
                            <div className="flex items-center gap-1 overflow-x-auto no-scrollbar pb-2">
                                {accountTypes.map((type) => (
                                    <button
                                        key={type}
                                        onClick={() => setActiveType(type)}
                                        className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-colors ${activeType === type
                                            ? 'bg-indigo-100 text-indigo-700'
                                            : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                                            }`}
                                    >
                                        {type}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Account List */}
                    <div className="h-[400px] overflow-y-auto p-2">
                        {isLoading ? (
                            <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-2">
                                <Loader2 className="w-5 h-5 animate-spin" />
                                <span className="text-sm">Loading QBO accounts...</span>
                            </div>
                        ) : displayAccounts.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-full text-gray-400 text-sm">
                                <p>No accounts found.</p>
                            </div>
                        ) : (
                            <div className="space-y-1">
                                {displayAccounts.map((account) => {
                                    const isSelected = account.data.account_id === currentAccountId;
                                    return (
                                        <button
                                            key={account._id}
                                            onClick={() => {
                                                onSelect(account);
                                                onClose();
                                            }}
                                            className={`flex items-start w-full text-left p-3 rounded-lg border transition-all group ${isSelected
                                                ? 'border-indigo-600 bg-indigo-50'
                                                : 'border-transparent hover:bg-gray-50 hover:border-gray-200'
                                                }`}
                                        >
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center justify-between mb-0.5">
                                                    <span className={`text-sm font-medium ${isSelected ? 'text-indigo-900' : 'text-gray-900'}`}>
                                                        {account.data.fully_qualified_name || account.data.name}
                                                    </span>
                                                    {isSelected && (
                                                        <span className="text-indigo-600">
                                                            <Check size={16} />
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-2 text-xs text-gray-500">
                                                    <span className="bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded font-mono">
                                                        #{account.data.account_id}
                                                    </span>
                                                    <span className="text-gray-400">• {account.data.account_type}</span>
                                                    {account.data.sub_type && (
                                                        <span className="text-gray-400">• {account.data.sub_type}</span>
                                                    )}
                                                </div>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default QBOAccountSelector;
